import { useState, type FormEvent } from "react";
import { heroStats, heroTags, searchSuggestions } from "@/mocks/home";

export default function Hero() {
  const [keyword, setKeyword] = useState("");
  const [hint, setHint] = useState("");

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = keyword.trim();
    if (!trimmed) {
      setHint("输入一个关键词，就能找到相关的讨论与观点。");
      return;
    }
    setHint(`已为你整理「${trimmed}」相关的讨论线索，可在下方快讯流中继续浏览。`);
  };

  return (
    <section className="relative overflow-hidden bg-foreground-950 pt-16 md:pt-20">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=abstract%20minimal%20warm%20paper%20texture%20background%20with%20soft%20flowing%20ink%20shapes%20in%20muted%20vermillion%20and%20amber%20tones%2C%20editorial%20magazine%20aesthetic%2C%20subtle%20grain%2C%20large%20empty%20space%2C%20artistic%20non%20realistic%20composition&width=1600&height=900&seq=hero-44talk-2026-01&orientation=landscape"
          alt="44黑料区热点讨论专区抽象背景"
          title="44黑料区 热点讨论 吃瓜评论"
          className="h-full w-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/92 via-foreground-950/80 to-foreground-950/55" />
      </div>

      <div className="relative mx-auto w-full px-4 py-12 md:px-6 md:py-20">
        <div className="flex flex-col gap-10 lg:flex-row lg:items-end lg:justify-between">
          <div className="w-full max-w-2xl rise-in">
            <div className="inline-flex items-center gap-2 rounded-full border border-background-50/25 bg-background-50/10 px-3 py-1.5">
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-pulse-line text-sm text-accent-400" />
              </span>
              <span className="font-label text-xs tracking-[0.16em] text-background-100">
                今日 312 个事件在榜 · 实时更新
              </span>
            </div>

            <h1 className="mt-5 font-heading text-3xl font-black leading-tight text-background-50 md:text-5xl">
              44黑料区
              <span className="mt-3 block text-lg font-bold text-accent-400 md:text-2xl">
                热点一起聊，观点各自说
              </span>
            </h1>

            <p className="mt-5 max-w-xl text-sm leading-relaxed text-background-200 md:text-base">
              这里汇聚网友真实评论与犀利观点。围绕最新热议事件，我们把时间线、讨论与反驳排在一起，
              让你先看过程，再决定信不信、怎么看。
            </p>

            <form onSubmit={handleSubmit} className="mt-7 w-full max-w-xl">
              <div className="flex flex-col gap-3 sm:flex-row">
                <div className="relative flex-1">
                  <span className="pointer-events-none absolute left-3 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center">
                    <i className="ri-search-line text-base text-foreground-500" />
                  </span>
                  <input
                    type="text"
                    name="keyword"
                    value={keyword}
                    onChange={(event) => setKeyword(event.target.value)}
                    placeholder="搜索热点事件、话题或关键词"
                    className="h-12 w-full rounded-md border border-background-50/20 bg-background-50/95 pl-10 pr-3 text-sm text-foreground-900 placeholder:text-foreground-500 focus:border-accent-400 focus:outline-none focus:ring-2 focus:ring-accent-400/40"
                  />
                </div>
                <button
                  type="submit"
                  className="h-12 whitespace-nowrap rounded-md bg-primary-500 px-6 font-label text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  搜索讨论
                </button>
              </div>
              {hint ? (
                <p className="mt-3 text-xs text-accent-300">{hint}</p>
              ) : (
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <span className="text-xs text-background-300">大家在搜：</span>
                  {searchSuggestions.map((item) => (
                    <button
                      key={item}
                      type="button"
                      onClick={() => {
                        setKeyword(item);
                        setHint("");
                      }}
                      className="whitespace-nowrap rounded-full border border-background-50/20 px-3 py-1 text-xs text-background-100 transition-colors hover:border-accent-400 hover:text-accent-300"
                    >
                      {item}
                    </button>
                  ))}
                </div>
              )}
            </form>

            <div className="mt-7 flex flex-wrap gap-2">
              {heroTags.map((tag) => (
                <a
                  key={tag}
                  href="#topics"
                  className="whitespace-nowrap rounded-full bg-background-50/10 px-3 py-1.5 text-xs text-background-200 transition-colors hover:bg-accent-500 hover:text-foreground-950"
                >
                  #{tag}
                </a>
              ))}
            </div>
          </div>

          <div className="w-full max-w-sm shrink-0 lg:pb-2 rise-in">
            <div className="rounded-lg border border-background-50/15 bg-background-50/5 p-5 backdrop-blur-sm">
              <div className="flex items-center justify-between">
                <span className="font-heading text-sm font-bold text-background-50">
                  今日讨论概览
                </span>
                <span className="flex items-center gap-1.5 text-xs text-accent-300">
                  <span className="h-1.5 w-1.5 rounded-full bg-accent-400" />
                  live
                </span>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-5">
                {heroStats.map((stat) => (
                  <div key={stat.label}>
                    <div className="font-heading text-2xl font-bold text-background-50">
                      {stat.value}
                    </div>
                    <div className="mt-1 text-xs text-background-300">{stat.label}</div>
                  </div>
                ))}
              </div>
              <a
                href="#live"
                className="mt-5 flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-50/25 py-2.5 text-sm text-background-100 transition-colors hover:bg-background-50/10"
              >
                进入实时快讯流
                <i className="ri-arrow-right-line" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}